/**
 * YAML Parser Utilities for Export/Import System
 *
 * Provides utilities for parsing YAML content and generating export filenames.
 * Note: JSON format is explicitly rejected - only YAML is accepted.
 *
 * Security: User YAML is validated before parse and loaded with JSON_SCHEMA only
 * (no custom types) to prevent code execution via malicious YAML (CWE-502).
 * This module is the single yaml.load() chokepoint for import flows and is an
 * accepted, reviewed exception to Wiz rule WS-JAVASCRIPT-00034 (alert #4101).
 */

import yaml from 'js-yaml'
import { MAX_IMPORT_FILE_SIZE } from '../constants/exportImport'
import { isAgentGraphPayload, isWorkflowConfiguration } from './graphImportKind'

const YAML_JSON_SCHEMA_OPTIONS = { schema: yaml.JSON_SCHEMA } as const

/** YAML tags/types that can trigger code execution when deserialized unsafely. */
const UNSAFE_YAML_TAG_PATTERN =
  /!!(?:js|python|java)[/:\w.-]*|tag:yaml\.org,2002:python/i

/**
 * Validate user YAML import text and return it for parsing.
 * Returning the validated value (rather than only asserting) lets yaml.load()
 * consume a checked string instead of the raw argument, which satisfies
 * taint-flow scanners (CWE-502).
 */
function sanitizeYamlImportContent(content: string): string {
  const trimmed = content.trim()
  if (!trimmed) {
    throw new yaml.YAMLException('Empty content - please provide valid YAML')
  }

  if (content.length > MAX_IMPORT_FILE_SIZE) {
    throw new yaml.YAMLException('YAML content exceeds maximum allowed size')
  }

  if (UNSAFE_YAML_TAG_PATTERN.test(content)) {
    throw new yaml.YAMLException('Unsupported YAML construct')
  }

  return content
}

/** Internal parse sink — only accepts sanitizeYamlImportContent output. */
function parseValidatedYamlText(validatedText: string): unknown {
  const { load } = yaml
  return load(validatedText, YAML_JSON_SCHEMA_OPTIONS) // wiz-ignore: WS-JAVASCRIPT-00034 nosemgrep: WS-JAVASCRIPT-00034
}

/**
 * Single chokepoint for parsing user-supplied YAML with a safe schema (js-yaml 4.x).
 */
export function loadYamlSafely(content: string): unknown {
  return parseValidatedYamlText(sanitizeYamlImportContent(content))
}

export interface ParseResult<T = unknown> {
  success: boolean
  data?: T
  error?: string
}

/**
 * Check if content appears to be JSON format (starts with { or [).
 * YAML is a superset of JSON, so we need to explicitly reject JSON-formatted content.
 */
function isJsonFormat(content: string): boolean {
  const trimmed = content.trim()
  // JSON typically starts with { or [ and the entire content is valid JSON
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed)
      return true // It's valid JSON
    } catch {
      // Not valid JSON, could be YAML that happens to start with { or [
      return false
    }
  }
  return false
}

/**
 * Parse YAML content to object.
 * Returns detailed error messages for malformed YAML.
 * Rejects JSON-formatted content - only YAML is accepted.
 */
export function parseYAML<T = unknown>(content: string): ParseResult<T> {
  try {
    const trimmed = content.trim()

    if (!trimmed) {
      return { success: false, error: 'Empty content - please provide valid YAML' }
    }

    // Reject JSON-formatted content - we only accept YAML
    if (isJsonFormat(trimmed)) {
      return {
        success: false,
        error: 'JSON format is not supported. Please convert your content to YAML format.',
      }
    }

    // Parse as YAML with safe schema (prevents code execution from malicious YAML)
    const data = loadYamlSafely(content) as T
    if (data === null || data === undefined) {
      return { success: true, data: {} as T }
    }
    return { success: true, data }
  } catch (e) {
    // Extract meaningful error info from YAML parsing errors
    if (e instanceof yaml.YAMLException) {
      const mark = e.mark
      if (mark) {
        return {
          success: false,
          error: `Invalid YAML at line ${mark.line + 1}, column ${mark.column + 1}: ${e.reason || e.message}`,
        }
      }
      return { success: false, error: `Invalid YAML: ${e.reason || e.message}` }
    }
    const error = e instanceof Error ? e.message : 'Unknown parsing error'
    return { success: false, error: `Failed to parse YAML: ${error}` }
  }
}

/**
 * Convert object to YAML string.
 */
export function toYAML(data: unknown): string {
  return yaml.dump(data, {
    indent: 2,
    lineWidth: 120,
    noRefs: true,
    sortKeys: false,
  })
}

/**
 * Convert object to JSON string.
 */
export function toJSON(data: unknown): string {
  return JSON.stringify(data, null, 2)
}

/**
 * Detect content format.
 */
export function detectFormat(content: string): 'yaml' | 'json' | 'unknown' {
  const trimmed = content.trim()

  // Check for JSON
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed)
      return 'json'
    } catch {
      // Not valid JSON
    }
  }

  // Try YAML (use same safe schema as parseYAML)
  try {
    loadYamlSafely(content)
    return 'yaml'
  } catch {
    // Not valid YAML
  }

  return 'unknown'
}

/**
 * Generate export filename with timestamp.
 * Format: {entity_type}_{name}_{datetime}.yaml
 * Note: format parameter kept for backward compatibility with exports.
 */
export function generateExportFilename(
  entityType: string,
  entityName: string,
  format: 'yaml' | 'json' = 'yaml'
): string {
  // Sanitize name: lowercase, replace spaces with hyphens, remove special chars
  const safeName = entityName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .substring(0, 50)

  // Generate timestamp
  const now = new Date()
  const timestamp = now.toISOString().replace(/[:.]/g, '-').substring(0, 19)

  const ext = format === 'yaml' ? 'yaml' : 'json'
  return `${entityType}_${safeName}_${timestamp}.${ext}`
}

/**
 * Download content as a file.
 */
export function downloadAsFile(
  content: string,
  filename: string,
  mimeType: string = 'text/yaml'
): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Validate YAML structure against expected fields.
 */
export function validateYAMLStructure(
  data: Record<string, unknown>,
  requiredFields: string[]
): { valid: boolean; missing: string[] } {
  const missing = requiredFields.filter((field) => !(field in data))
  return {
    valid: missing.length === 0,
    missing,
  }
}

/**
 * Check if data is a bundle (multiple entity types).
 */
export function isBundle(data: Record<string, unknown>): boolean {
  const meta = data._meta as Record<string, unknown> | undefined
  if (meta?.bundle_type) {
    return true
  }

  const bundleKeys = [
    'agents',
    'models',
    'tools',
    'prompts',
    'skills',
    'knowledge',
    'connectors',
    'iam',
  ]
  return bundleKeys.some((key) => Array.isArray(data[key]))
}

/**
 * Extract entity type from YAML data.
 */
export function extractEntityType(data: Record<string, unknown>): string | null {
  // Workflow graphs share nodes/edges with agents — inspect type metadata first.
  if (isWorkflowConfiguration(data)) {
    return 'workflow'
  }

  // Check _meta first. Legacy Studio YAML used entity_type: workflow as an
  // agent alias; only official AI Workflows docs (trigger_config / workflow
  // node types) should classify as workflow — handled above.
  const meta = data._meta as Record<string, unknown> | undefined
  const metaType = typeof meta?.entity_type === 'string' ? meta.entity_type : ''
  const metaTypeLower = metaType.toLowerCase()
  if (metaType && metaTypeLower !== 'workflow' && metaTypeLower !== 'workflows') {
    return metaType
  }

  // Infer from data structure (workflow graphs already returned above)
  if ('graph_definition' in data) return 'agent'
  if (isAgentGraphPayload(data)) return 'agent'
  if ('provider' in data && 'model_id' in data) return 'model'
  if ('endpoint' in data && 'schema' in data) return 'tool'
  if ('connector_ref' in data || 'retrieval_config' in data) return 'knowledge'
  if ('connector_type' in data && 'host' in data) return 'connector'
  if ('cloud_provider' in data && 'iam_subtype' in data) return 'iam'
  if ('skill_md_content' in data || 'slug' in data) return 'skill'
  if ('versions' in data || ('name' in data && 'content' in data)) return 'prompt'

  return null
}
